import React, { useState, useEffect, Suspense, lazy } from 'react';
// The toast layer is only ever needed after someone acts, so it is not part of the
// download that has to finish before the first fact can appear.
const Toaster = lazy(() => import('@/components/ui/toaster').then(m => ({ default: m.Toaster })));
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from '@/components/ProtectedRoute';
// Auth screens (and the OTP input they pull in) only load when someone signs in.
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
// Every page loads on demand, so a post URL never downloads the feed's code (and vice versa).
const Home = lazy(() => import('@/pages/Home'));
const PostDetail = lazy(() => import('@/pages/PostDetail'));
const Compose = lazy(() => import('@/pages/Compose'));
const Explore = lazy(() => import('@/pages/Explore'));
const TopicPage = lazy(() => import('@/pages/TopicPage'));
const Profile = lazy(() => import('@/pages/Profile'));
const EditProfile = lazy(() => import('@/pages/EditProfile'));
const Admin = lazy(() => import('@/pages/Admin'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const SavedPosts = lazy(() => import('@/pages/SavedPosts'));
const Guidelines = lazy(() => import('@/pages/Guidelines'));
const About = lazy(() => import('@/pages/About'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Cookies = lazy(() => import('@/pages/Cookies'));
const Contact = lazy(() => import('@/pages/Contact'));
const Welcome = lazy(() => import('@/pages/Welcome'));
const Business = lazy(() => import('@/pages/Business'));
const ChallengePage = lazy(() => import('@/pages/ChallengePage'));
const Achievements = lazy(() => import('@/pages/Achievements'));

const PageFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="w-8 h-8 border-4 border-secondary border-t-primary rounded-full animate-spin" />
  </div>
);
import { TranslationProvider } from '@/hooks/useTranslation';
import { dirFor } from '@/lib/rtl';
import { isLanguageEnabled } from '@/lib/languages';
// None of these draw the fact the reader came for: tracking, ads, the cookie notice, the
// install nudge and pull-to-refresh all belong after the page exists, so their code is
// fetched separately instead of inside the bundle that blocks the first paint.
const InstallPrompt = lazy(() => import('@/components/InstallPrompt'));
const PullToRefresh = lazy(() => import('@/components/PullToRefresh'));
const CookieBanner = lazy(() => import('@/components/CookieBanner'));
const VisitTracker = lazy(() => import('@/components/VisitTracker'));
const AdsenseLoader = lazy(() => import('@/components/ads/AdsenseLoader'));
const GoogleAnalytics = lazy(() => import('@/components/GoogleAnalytics'));
const GuestChallenge = lazy(() => import('@/components/guest/GuestChallenge'));
const ChallengeNudge = lazy(() => import('@/components/challenge/ChallengeNudge'));

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  const [currentLang, setCurrentLang] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem('wkt_lang');
    if (saved) {
      // A language that was on when the reader picked it may have been switched off since.
      // Their browser would otherwise keep requesting translations and minting /xx/ links
      // for a language the site no longer publishes, so the stale choice is dropped.
      if (saved === 'en') { setCurrentLang('en'); return; }
      isLanguageEnabled(saved).then(ok => {
        if (ok) setCurrentLang(saved);
        else localStorage.removeItem('wkt_lang');
      });
      return;
    }
    // Auto-detect the browser language, but only honour it when the admin enabled it —
    // an unsupported language would otherwise generate translations and indexable URLs.
    const browserLang = (navigator.language || 'en').split('-')[0].toLowerCase();
    if (browserLang !== 'en') {
      isLanguageEnabled(browserLang).then(ok => {
        if (!ok) return;
        setCurrentLang(browserLang);
        localStorage.setItem('wkt_lang', browserLang);
      });
    }
  }, []);

  // Reading direction follows the chosen language everywhere. The lang attribute is left to
  // each page: a post page must declare the language its text is actually served in, which
  // is not always the one selected in the menu.
  useEffect(() => {
    document.documentElement.dir = dirFor(currentLang);
  }, [currentLang]);

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
    localStorage.setItem('wkt_lang', lang);
  };

  // Public pages render immediately; the login check finishes in the background.
  // Login-only pages wait on it themselves through ProtectedRoute.
  if (!isLoadingPublicSettings && !isLoadingAuth && authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <TranslationProvider currentLang={currentLang}>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Home currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/post/:slug" element={<PostDetail currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/:lang/:topic/:slug" element={<PostDetail currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/explore" element={<Explore currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/topic/:slug" element={<TopicPage currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/leaderboard" element={<Navigate to="/" replace />} />
        <Route path="/challenge/:code" element={<ChallengePage currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/guidelines" element={<Guidelines currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/about" element={<About currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/terms" element={<Terms currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/privacy" element={<Privacy currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/cookies" element={<Cookies currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route path="/contact" element={<Contact currentLang={currentLang} onLangChange={handleLangChange} />} />
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
          <Route path="/compose" element={<Compose currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/profile" element={<Profile currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/achievements" element={<Achievements currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/notifications" element={<Notifications currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/saved" element={<SavedPosts currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/profile/edit" element={<EditProfile currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/business" element={<Business currentLang={currentLang} onLangChange={handleLangChange} />} />
          <Route path="/admin" element={<Admin currentLang={currentLang} onLangChange={handleLangChange} />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <VisitTracker />
        <GoogleAnalytics currentLang={currentLang} />
        <AdsenseLoader />
        <PullToRefresh />
        <InstallPrompt />
        <CookieBanner />
        <GuestChallenge />
        <ChallengeNudge />
      </Suspense>
    </TranslationProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Suspense fallback={null}><Toaster /></Suspense>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App